import { Command, Spinner } from '@ntconsult/aluma-react';
import { ChevronRight, FileVideo, Folder as FolderIcon, User as UserIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import type { SearchFileResult, SearchFolderResult, SearchUserResult } from '../types';

type ItemKind = 'file' | 'folder' | 'user';

const VIEW_ALL_VALUE = '__view_all__';

function buildValue(kind: ItemKind, id: string): string {
  return `${kind}:${id}`;
}

function navigateToFor(value: string, viewAllTarget: string | null): string | null {
  if (value === VIEW_ALL_VALUE) return viewAllTarget;
  const sep = value.indexOf(':');
  if (sep < 0) return null;
  const kind = value.slice(0, sep) as ItemKind;
  const payload = value.slice(sep + 1);
  if (!payload) return null;
  switch (kind) {
    case 'file':
      return `/files/view/${payload}`;
    case 'folder':
      return `/files/${payload}`;
    case 'user':
      return `/users?search=${encodeURIComponent(payload)}`;
    default:
      return null;
  }
}

interface GlobalSearchProps {
  /**
   * Quando true, renderiza input + lista empilhados (sem popover absoluto) —
   * usado dentro do modal mobile, onde o popover seria clipado.
   */
  inline?: boolean;
  /** Foco automático no input. Útil ao abrir dentro de um Modal. */
  autoFocus?: boolean;
  /** Disparado ao selecionar um resultado (para fechar o modal). */
  onNavigate?: () => void;
}

export function GlobalSearch({ inline = false, autoFocus = false, onNavigate }: GlobalSearchProps = {}) {
  const { t } = useTranslation(['common', 'files']);
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(inline);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isReady, debouncedQuery } = useGlobalSearch(query);

  // Cmd/Ctrl + K foca o input em qualquer página (desativado no modo inline,
  // pois lá o input já abre via Modal próprio).
  useEffect(() => {
    if (inline) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [inline]);

  // Foco inicial no modo inline (ex.: ao abrir o modal mobile).
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  // Fecha ao clicar fora (apenas no modo popover).
  useEffect(() => {
    if (inline || !open) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, inline]);

  const files = data?.results.files ?? [];
  const folders = data?.results.folders ?? [];
  const users = data?.results.users ?? [];

  const hasAny = files.length > 0 || folders.length > 0 || users.length > 0;

  const viewAllTarget = useMemo(
    () =>
      debouncedQuery.length >= 2 ? `/files?search=${encodeURIComponent(debouncedQuery)}` : null,
    [debouncedQuery]
  );

  const handleSelect = useCallback(
    (value: string) => {
      const target = navigateToFor(value, viewAllTarget);
      if (!target) return;
      if (!inline) setOpen(false);
      setQuery('');
      navigate(target);
      onNavigate?.();
    },
    [navigate, viewAllTarget, inline, onNavigate]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (!inline) {
        setOpen(false);
        inputRef.current?.blur();
      }
      return;
    }
    if (event.key === 'Enter' && !hasAny && debouncedQuery.length >= 2) {
      event.preventDefault();
      if (!inline) setOpen(false);
      navigate(`/files?search=${encodeURIComponent(debouncedQuery)}`);
      onNavigate?.();
    }
  };

  const showPopover = (inline || open) && isReady;
  const showEmpty = showPopover && !isFetching && !hasAny;

  // O `Command` do Aluma é uma caixa única (input + list) com `overflow-hidden`.
  // No modo popover (desktop), ele é renderizado em `absolute` dentro de um
  // wrapper de altura fixa para não empurrar o layout do header. No modo
  // `inline` (mobile dentro de Modal), é renderizado empilhado e a lista cresce
  // para preencher o espaço — o Modal pai já limita a altura por safe-area.
  const wrapperClass = inline
    ? 'w-full flex flex-col min-h-0 flex-1'
    : 'w-full max-w-xl h-11 relative isolate';
  const commandClass = inline
    ? 'flex flex-col min-h-0 flex-1 border-none'
    : `absolute inset-x-0 top-0 z-50 ${showPopover ? 'border-none' : ''}`;
  const listMaxClass = inline ? 'flex-1 min-h-0' : 'max-h-[60vh]';

  return (
    <div ref={containerRef} className={wrapperClass}>
      <Command
        search={query}
        onSearchChange={setQuery}
        filter={() => true}
        onItemSelect={handleSelect}
        label={t('common:search.label', { defaultValue: 'Busca global' })}
        className={commandClass}
      >
        <Command.Input
          ref={inputRef}
          placeholder={t('common:search.placeholder', {
            defaultValue: 'Buscar arquivos, pastas e pessoas…',
          })}
          aria-label={t('common:search.label', { defaultValue: 'Busca global' })}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />

        {showPopover && (
          <Command.List className={listMaxClass}>
            {isFetching && !hasAny && (
              <div className="flex items-center justify-center gap-2 px-3 py-6 text-sm text-foreground-muted">
                <Spinner size="sm" />
                <span>{t('common:search.loading', { defaultValue: 'Buscando…' })}</span>
              </div>
            )}

            {folders.length > 0 && (
              <Command.Group
                heading={t('common:search.groups.folders', {
                  defaultValue: 'Pastas',
                })}
              >
                {folders.map(renderFolderItem)}
              </Command.Group>
            )}

            {files.length > 0 && (
              <Command.Group
                heading={t('common:search.groups.files', {
                  defaultValue: 'Arquivos',
                })}
              >
                {files.map(renderFileItem)}
              </Command.Group>
            )}

            {users.length > 0 && (
              <Command.Group
                heading={t('common:search.groups.users', {
                  defaultValue: 'Pessoas',
                })}
              >
                {users.map(renderUserItem)}
              </Command.Group>
            )}

            {showEmpty && (
              <Command.Empty>
                {t('common:search.empty', {
                  defaultValue: 'Nenhum resultado para',
                })}{' '}
                <span className="font-medium text-foreground">“{debouncedQuery}”</span>
              </Command.Empty>
            )}

            {viewAllTarget && (
              <>
                <Command.Separator />
                <Command.Item value={VIEW_ALL_VALUE}>
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  <span>
                    {t('common:search.viewAll', {
                      defaultValue: 'Ver todos os resultados de',
                    })}{' '}
                    “{debouncedQuery}”
                  </span>
                </Command.Item>
              </>
            )}
          </Command.List>
        )}
      </Command>
    </div>
  );
}

function renderFileItem(file: SearchFileResult) {
  const value = buildValue('file', file.id);
  return (
    <Command.Item key={value} value={value} keywords={[file.name, file.folderName ?? '']}>
      <FileVideo className="w-4 h-4 text-foreground-muted shrink-0" aria-hidden="true" />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate text-sm text-foreground">{file.name}</span>
        {file.folderName && (
          <span className="truncate text-xs text-foreground-muted">{file.folderName}</span>
        )}
      </div>
    </Command.Item>
  );
}

function renderFolderItem(folder: SearchFolderResult) {
  const value = buildValue('folder', folder.id);
  return (
    <Command.Item key={value} value={value} keywords={[folder.name]}>
      <FolderIcon className="w-4 h-4 text-foreground-muted shrink-0" aria-hidden="true" />
      <span className="truncate text-sm text-foreground">{folder.name}</span>
    </Command.Item>
  );
}

function renderUserItem(user: SearchUserResult) {
  const value = buildValue('user', user.email);
  return (
    <Command.Item key={value} value={value} keywords={[user.name, user.email]}>
      <UserIcon className="w-4 h-4 text-foreground-muted shrink-0" aria-hidden="true" />
      <div className="flex flex-col min-w-0 flex-1">
        <span className="truncate text-sm text-foreground">{user.name}</span>
        <span className="truncate text-xs text-foreground-muted">{user.email}</span>
      </div>
    </Command.Item>
  );
}
